/**
 * Select Component
 *
 * Reusable select dropdown using Equal DS Dropdown components
 */

import React, { useState } from 'react';
import {
  Dropdown,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
} from 'equal-ds-ui';
export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const Select: React.FC<SelectProps> = ({
  value,
  options,
  onChange,
  placeholder = 'Select...',
  className = '',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Find the selected option label
  const selectedOption = options.find(opt => opt.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setIsOpen(false);
  };

  return (
    <div className={`equal-ds-root ${className}`}>
      <Dropdown open={isOpen} onOpenChange={setIsOpen}>
        <DropdownTrigger
          disabled={disabled}
          className={`
            w-full px-3 py-2
            bg-white border border-gray-200 rounded-lg
            text-left text-sm text-gray-900
            hover:bg-gray-50 hover:border-gray-300
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            disabled:bg-gray-100 disabled:cursor-not-allowed
            transition-colors duration-200
            flex items-center justify-between gap-2
          `}
        >
          <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
            {displayText}
          </span>
        </DropdownTrigger>

        {/* DropdownContent already includes Portal internally */}
        <DropdownContent
          className="
            min-w-[200px] max-h-[300px] overflow-y-auto
            bg-white border border-gray-200 rounded-lg shadow-lg
            py-1
            z-[9999]
          "
          align="start"
          sideOffset={4}
        >
          {options.map((option) => (
            <DropdownItem
              key={option.value}
              onSelect={() => handleSelect(option.value)}
              className={`
                px-3 py-2 text-sm cursor-pointer
                hover:bg-blue-50 hover:text-blue-900
                focus:bg-blue-50 focus:text-blue-900 focus:outline-none
                ${option.value === value ? 'bg-blue-100 text-blue-900 font-medium' : 'text-gray-700'}
              `}
            >
              {option.label}
            </DropdownItem>
          ))}
        </DropdownContent>
      </Dropdown>
    </div>
  );
};
